/**
 * Правила сетки Раскладки — ядро редактора (спецификация § 4, тикет T18).
 *
 * Здесь живёт всё, что происходит, когда Администратор двигает Блок мышью, тянет его за
 * угол или добавляет новый из каталога. Ядро не знает ни про React, ни про браузер
 * (ADR-0002): на вход приходят числа клеток, на выход — новая расстановка. Ту же самую
 * расстановку потом покажет и настольное приложение, и телефон.
 *
 * Раскладка — это данные, а не вёрстка: Блок стоит в клетках сетки, а не в пикселях.
 * Пиксели у каждого экрана свои, а клетки одни и те же.
 *
 * Два Блока не могут стоять на одном месте. Сервер этого не запрещает — он проверяет
 * только границы, — поэтому непересечение держит редактор: человек, двигающий Блок,
 * должен видеть, куда разъехались соседи, ещё до того, как отпустит мышь.
 */

/** Ширина сетки в клетках. То же число, что на сервере: расходиться им нельзя. */
export const GRID_COLUMNS = 12;

/** Предел Блоков на Раскладке. Больше — экран отрисовывается медленно. */
export const BLOCK_LIMIT = 24;

/** Место Блока на сетке: левый край, верх, ширина и высота — всё в клетках. */
export interface Position {
  readonly col: number;
  readonly row: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Блок глазами редактора. Настройки и Тип редактору сетки не нужны: он двигает
 * прямоугольники, а что внутри — дело оболочки.
 */
export interface PlacedBlock {
  readonly id: string;
  readonly position: Position;
}

/** Что случилось с расстановкой и что об этом сказать человеку. */
export interface GridChange {
  readonly blocks: readonly PlacedBlock[];
  /** Пусто — всё получилось. Иначе объяснение словами, без слов из машинного мира. */
  readonly refusal: string | null;
}

const done = (blocks: readonly PlacedBlock[]): GridChange => ({ blocks, refusal: null });

const refused = (blocks: readonly PlacedBlock[], refusal: string): GridChange => ({
  blocks,
  refusal,
});

/** Пересекаются ли два места. Касание краями пересечением не считается. */
export const overlap = (a: Position, b: Position): boolean =>
  a.col < b.col + b.width &&
  b.col < a.col + a.width &&
  a.row < b.row + b.height &&
  b.row < a.row + a.height;

/**
 * Целые клетки, хотя бы одна, и не за правым краем. Те же правила, что на сервере:
 * человек не должен узнавать о неверном размере только после отправки.
 */
const checkPosition = (position: Position): string | null => {
  const { col, row, width, height } = position;
  if (![col, row, width, height].every((n) => Number.isSafeInteger(n))) {
    return 'Блок ставится на целые клетки сетки';
  }
  if (col < 0 || row < 0 || width < 1 || height < 1) {
    return 'Блок занимает хотя бы одну клетку сетки';
  }
  if (col + width > GRID_COLUMNS) {
    return `В сетке ${GRID_COLUMNS} клеток по ширине — так Блок выйдет за край экрана и его не будет видно`;
  }
  return null;
};

/** Порядок сверху вниз, при равенстве — слева направо. Так же читают экран глазами. */
const byPlace = (a: PlacedBlock, b: PlacedBlock): number =>
  a.position.row - b.position.row || a.position.col - b.position.col;

/**
 * Поднять Блоки вверх, пока под ними пустота. Иначе после каждого перетаскивания в
 * Раскладке оставались бы дыры, а Блоки медленно уползали бы вниз экрана.
 *
 * `pinned` остаётся там, куда его поставил человек: Блок, который сейчас держат мышью,
 * не должен уезжать из-под курсора.
 */
export const compact = (
  blocks: readonly PlacedBlock[],
  pinned: string | null = null,
): PlacedBlock[] => {
  const settled: PlacedBlock[] = [];
  for (const block of [...blocks].sort(byPlace)) {
    if (block.id === pinned) {
      settled.push(block);
      continue;
    }
    let row = block.position.row;
    while (row > 0) {
      const higher = { ...block.position, row: row - 1 };
      if (settled.some((other) => overlap(higher, other.position))) break;
      row -= 1;
    }
    settled.push({ ...block, position: { ...block.position, row } });
  }
  return settled;
};

/**
 * Развести Блоки, которые встали друг на друга: потеснённый уезжает вниз, а не исчезает
 * и не ложится поверх. Двигают всегда соседа, а не того, кого держит человек, — иначе
 * Блок выскакивал бы из-под курсора.
 *
 * Считается по кругу, пока пересечений не останется: сдвинутый Блок сам может задеть
 * следующего, и остановиться на первом круге значит оставить два Блока в одной клетке.
 */
const settle = (blocks: readonly PlacedBlock[], movedId: string): PlacedBlock[] => {
  // Черновик расстановки: место здесь переписывается на месте, поэтому оно не `readonly`.
  // Меняются только эти копии — пришедший массив и его Блоки остаются нетронутыми, иначе
  // отказ на середине оставил бы Раскладку наполовину разъехавшейся.
  const placed: Array<{ id: string; position: Position }> = blocks.map((block) => ({
    id: block.id,
    position: block.position,
  }));
  const moved = placed.find((block) => block.id === movedId);
  if (!moved) return placed;

  // Кругов не больше, чем Блоков: каждый круг опускает хотя бы один Блок ниже всех
  // тех, с кем он спорил, поэтому дальше этого числа спорить уже не с кем.
  for (let round = 0; round <= placed.length; round += 1) {
    let shifted = false;
    // Разбор идёт сверху вниз: нижние уступают верхним, а не наоборот.
    for (const block of [...placed].sort(byPlace)) {
      for (const other of placed) {
        if (other.id === block.id) continue;
        if (!overlap(block.position, other.position)) continue;
        // Уступает тот, кого не держат мышью; при равных правах — тот, кто ниже.
        const gives =
          other.id === moved.id
            ? block
            : block.id === moved.id
              ? other
              : byPlace(block, other) <= 0
                ? other
                : block;
        const keeps = gives.id === block.id ? other : block;
        gives.position = {
          ...gives.position,
          row: keeps.position.row + keeps.position.height,
        };
        shifted = true;
      }
    }
    if (!shifted) break;
  }
  return compact(placed, movedId);
};

/**
 * Передвинуть Блок. Место проверяется до сдвига соседей: отказ должен оставлять
 * Раскладку ровно такой, какой она была, а не наполовину разъехавшейся.
 */
export function moveBlock(
  blocks: readonly PlacedBlock[],
  id: string,
  to: { col: number; row: number },
): GridChange {
  const target = blocks.find((block) => block.id === id);
  if (!target) return refused(blocks, 'Такого Блока на Раскладке нет');

  const position: Position = { ...target.position, col: to.col, row: to.row };
  const wrong = checkPosition(position);
  if (wrong) return refused(blocks, wrong);

  const next = blocks.map((block) => (block.id === id ? { ...block, position } : block));
  return done(settle(next, id));
}

/**
 * Изменить размер Блока. Наименьший размер приходит из каталога Типов: у Переписки и у
 * Котировок он разный, и знать об этом сетке неоткуда.
 */
export function resizeBlock(
  blocks: readonly PlacedBlock[],
  id: string,
  size: { width: number; height: number },
  minimum: { width: number; height: number } = { width: 1, height: 1 },
): GridChange {
  const target = blocks.find((block) => block.id === id);
  if (!target) return refused(blocks, 'Такого Блока на Раскладке нет');

  const position: Position = { ...target.position, width: size.width, height: size.height };
  const wrong = checkPosition(position);
  if (wrong) return refused(blocks, wrong);
  if (size.width < minimum.width || size.height < minimum.height) {
    return refused(
      blocks,
      `Меньше ${minimum.width} на ${minimum.height} этот Блок делать нельзя — в нём ничего не будет видно`,
    );
  }

  const next = blocks.map((block) => (block.id === id ? { ...block, position } : block));
  return done(settle(next, id));
}

/**
 * Найти место для нового Блока. Ищем сверху вниз и слева направо — первое свободное
 * место, куда он целиком помещается. Так новый Блок появляется там, куда смотрит
 * человек, а не в конце длинного экрана.
 */
export const freeSpot = (
  blocks: readonly PlacedBlock[],
  size: { width: number; height: number },
): Position => {
  const width = Math.min(Math.max(1, size.width), GRID_COLUMNS);
  const height = Math.max(1, size.height);
  const bottom = blocks.reduce(
    (lowest, block) => Math.max(lowest, block.position.row + block.position.height),
    0,
  );
  for (let row = 0; row <= bottom; row += 1) {
    for (let col = 0; col + width <= GRID_COLUMNS; col += 1) {
      const spot: Position = { col, row, width, height };
      if (!blocks.some((block) => overlap(spot, block.position))) return spot;
    }
  }
  // Свободного места на занятой части экрана не нашлось — Блок встаёт под всеми.
  return { col: 0, row: bottom, width, height };
};

/**
 * Добавить Блок из каталога. Место можно не выбирать: человек нажимает «добавить», а
 * куда встанет — считает редактор. Предел в 24 Блока проверяется здесь же, чтобы отказ
 * пришёл до похода на сервер, а не после.
 */
export function addBlock(
  blocks: readonly PlacedBlock[],
  block: { id: string; size: { width: number; height: number }; at?: { col: number; row: number } },
): GridChange {
  if (blocks.length >= BLOCK_LIMIT) {
    return refused(
      blocks,
      `На Раскладке уже ${BLOCK_LIMIT} Блока — это предел, иначе экран будет отрисовываться медленно. Уберите ненужный Блок и добавляйте новый.`,
    );
  }
  if (blocks.some((one) => one.id === block.id)) {
    return refused(blocks, 'Такой Блок на Раскладке уже стоит');
  }
  // Размер проверяем до поиска места: `freeSpot` подрезал бы слишком широкий Блок под
  // ширину сетки молча, и человек получил бы не тот Блок, который просил.
  if (block.size.width > GRID_COLUMNS) {
    return refused(
      blocks,
      `В сетке ${GRID_COLUMNS} клеток по ширине — такой Блок в неё не помещается`,
    );
  }

  const spot = block.at
    ? { ...block.at, width: block.size.width, height: block.size.height }
    : freeSpot(blocks, block.size);
  const wrong = checkPosition(spot);
  if (wrong) return refused(blocks, wrong);

  const next = [...blocks, { id: block.id, position: spot }];
  return done(settle(next, block.id));
}

/**
 * Убрать Блок. Оставшиеся поднимаются на освободившееся место: дыра посреди экрана
 * выглядит как поломка, хотя ничего не сломалось.
 */
export function removeBlock(blocks: readonly PlacedBlock[], id: string): GridChange {
  if (!blocks.some((block) => block.id === id)) {
    return refused(blocks, 'Такого Блока на Раскладке нет');
  }
  return done(compact(blocks.filter((block) => block.id !== id)));
}

/**
 * Что из этого нужно отправить на сервер. Сравнение идёт по местам, а не по всему
 * подряд: перетащили один Блок, а поехали трое — сохранить надо всех троих и только их.
 */
export const movedBlocks = (
  before: readonly PlacedBlock[],
  after: readonly PlacedBlock[],
): PlacedBlock[] => {
  const was = new Map(before.map((block) => [block.id, block.position]));
  return after.filter((block) => {
    const previous = was.get(block.id);
    if (!previous) return true;
    return (
      previous.col !== block.position.col ||
      previous.row !== block.position.row ||
      previous.width !== block.position.width ||
      previous.height !== block.position.height
    );
  });
};
